import { useState, useCallback } from 'react';
import styled from '@emotion/styled';
import { tokens, Text, DateRangePicker, Link } from '@hotjar/hotsauce';

import { useHelpHotjarUrl } from '@hotjar/translations';
import type { AbsoluteDateRange, DateRange, MinimalDateConstraint } from './DateRangeFilterType';
import { DATE_RANGE_TYPE } from './DateRangeFilterType';
import { GenericFilterFooter } from '../GenericBloc/GenericFilter/GenericFilterFooter';
import { GenericFilterBody } from '../GenericBloc/GenericFilter/GenericFilterBody';
import { GenericFilterFooterMessage } from '../GenericBloc/GenericFilter/GenericFilterFooterMessage';
import {
  FilterItemRadio,
  FilterRadioContainer,
} from '../GenericBloc/GenericSubElement/FilterItemRadio';
import { GenericFilterDropdown } from '../GenericBloc/GenericFilter/GenericFilterDropdown';
import type { AbstractSingleFilterDropdownProps } from '../SingleFilterType';
import {
  equalsDateRange,
  makeAbsoluteDateRange,
  makeAllDateRange,
  makeRelativeDateRange,
  isDateRangeBeforeMinDate,
  getMinDateMessage,
} from './DateRangeFilterUtils';
import { validateValue, ValidationErrorEnum } from './DateRangeFilterValidator';
import { DEFAULT_VALUE, RELATIVE_DATE_RANGES } from './constant';
import { ALL_DATES_LABEL, getDateRangeLabel } from './DateRangeFilterFormatter';

const DatePickerContainer = styled.div`
  padding-bottom: ${tokens.spacer.x1};
  padding-left: 23px;
`;

const ErrorWrapper = styled.div`
  padding-top: ${tokens.spacer.x0};
`;

interface DateRangeDropdownProps extends AbstractSingleFilterDropdownProps<DateRange> {
  hideAllDatesOption?: boolean;
  dateConstraint?: MinimalDateConstraint;
}

export function DateRangeDropdown({
  currentValue,
  optionalDefaultValue = DEFAULT_VALUE,
  dateConstraint,
  onApply,
  hideAllDatesOption = false,
  filterName,
}: DateRangeDropdownProps): JSX.Element {
  const helpHotjarUrl = useHelpHotjarUrl();
  const [dirtyValue, setDirtyValue] = useState<DateRange | undefined>(
    currentValue ?? optionalDefaultValue,
  );
  const [dirtyCustomDateRange, setDirtyCustomDateRange] = useState<AbsoluteDateRange | undefined>(
    currentValue?.type === DATE_RANGE_TYPE.ABSOLUTE ? currentValue : undefined,
  );
  const [validationError, setValidationError] = useState<ValidationErrorEnum>();
  const handleChange = useCallback(
    (newValue) => {
      setDirtyValue(newValue);
      setValidationError(undefined);
    },
    [setDirtyValue, setValidationError],
  );

  const handleCustomDateChange = useCallback(
    (newValue) => {
      if (newValue != null) {
        setDirtyCustomDateRange(makeAbsoluteDateRange(newValue.from, newValue.to));
        setValidationError(undefined);
      }
    },
    [setDirtyCustomDateRange, setValidationError],
  );

  const handleApply = useCallback(() => {
    const optionalError = validateValue(dirtyValue, dirtyCustomDateRange, dateConstraint?.minDate);
    if (optionalError) {
      setValidationError(optionalError);
    } else if (dirtyCustomDateRange && dirtyValue?.type === DATE_RANGE_TYPE.ABSOLUTE) {
      // If `from` is after `to` or vice versa, we swap them
      const from =
        dirtyCustomDateRange.from < dirtyCustomDateRange.to
          ? dirtyCustomDateRange.from
          : dirtyCustomDateRange.to;
      const to =
        dirtyCustomDateRange.from < dirtyCustomDateRange.to
          ? dirtyCustomDateRange.to
          : dirtyCustomDateRange.from;

      onApply(makeAbsoluteDateRange(from, to));
    } else {
      onApply(dirtyValue);
    }
  }, [onApply, dirtyValue, dirtyCustomDateRange, dateConstraint]);

  let contraintToShowInFooter = currentValue?.autoSelectedConstraint;
  if (!contraintToShowInFooter && dateConstraint?.isFilterRelated === true) {
    contraintToShowInFooter = dateConstraint;
  }
  const footerMessage = contraintToShowInFooter ? (
    <GenericFilterFooterMessage>
      <span>{contraintToShowInFooter.reason} </span>
      {contraintToShowInFooter.link && (
        <Link
          kind="inline"
          href={`${helpHotjarUrl}${contraintToShowInFooter.link.href}`}
          isExternal
        >
          {contraintToShowInFooter.link.text}
        </Link>
      )}
    </GenericFilterFooterMessage>
  ) : null;

  return (
    <GenericFilterDropdown
      ariaLabel={`${filterName} dropdown`}
      customWidth={370}
      onApply={handleApply}
    >
      <GenericFilterBody>
        <FilterRadioContainer>
          {RELATIVE_DATE_RANGES.map((range) => {
            const key = `date-range-relative-${range.amount}-${range.unit}`;
            return (
              <FilterItemRadio
                isChecked={equalsDateRange(
                  dirtyValue,
                  makeRelativeDateRange(range.amount, range.unit),
                )}
                id={key}
                key={key}
                name="date-range-option"
                onChange={() => handleChange(range)}
                labelContent={getDateRangeLabel(range)}
                isInvalid={validationError === ValidationErrorEnum.SELECT_ONE_OPTION}
                isDisabled={isDateRangeBeforeMinDate(range, dateConstraint)}
                tooltipValue={getMinDateMessage(range, dateConstraint)}
              />
            );
          })}
          <FilterItemRadio
            isChecked={dirtyValue?.type === DATE_RANGE_TYPE.ABSOLUTE}
            id="date-range-absolute"
            name="date-range-option"
            onChange={() => {
              handleChange(makeAbsoluteDateRange(new Date(), new Date()));
            }}
            labelContent="Custom date range"
            isInvalid={validationError === ValidationErrorEnum.SELECT_ONE_OPTION}
          />
          {dirtyValue?.type === DATE_RANGE_TYPE.ABSOLUTE && (
            <DatePickerContainer>
              <DateRangePicker
                fromName={`${filterName} from`}
                toName={`${filterName} to`}
                range={dirtyCustomDateRange}
                onDateChange={handleCustomDateChange}
                fromIsInvalid={
                  validationError === ValidationErrorEnum.INVALID_CUSTOM_DATE ||
                  validationError === ValidationErrorEnum.INVALID_CUSTOM_FROM_DATE ||
                  validationError === ValidationErrorEnum.FROM_DATE_BEFORE_MIN_DATE
                }
                toIsInvalid={
                  validationError === ValidationErrorEnum.INVALID_CUSTOM_DATE ||
                  validationError === ValidationErrorEnum.INVALID_CUSTOM_TO_DATE ||
                  validationError === ValidationErrorEnum.TO_DATE_BEFORE_MIN_DATE
                }
                preventFutureDate
                preventDatesBefore={dateConstraint?.minDate}
              />
              {(validationError === ValidationErrorEnum.INVALID_CUSTOM_DATE ||
                validationError === ValidationErrorEnum.INVALID_CUSTOM_FROM_DATE ||
                validationError === ValidationErrorEnum.INVALID_CUSTOM_TO_DATE ||
                validationError === ValidationErrorEnum.FROM_DATE_BEFORE_MIN_DATE ||
                validationError === ValidationErrorEnum.TO_DATE_BEFORE_MIN_DATE) && (
                <ErrorWrapper>
                  <Text textColor="text_error">
                    {validationError === ValidationErrorEnum.FROM_DATE_BEFORE_MIN_DATE ||
                    validationError === ValidationErrorEnum.TO_DATE_BEFORE_MIN_DATE ? (
                      <>
                        <span>{dateConstraint?.reason} </span>
                        {dateConstraint?.link && (
                          <Link
                            kind="inline"
                            href={`${helpHotjarUrl}${dateConstraint.link.href}`}
                            isExternal
                          >
                            {dateConstraint.link.text}
                          </Link>
                        )}
                      </>
                    ) : (
                      validationError
                    )}
                  </Text>
                </ErrorWrapper>
              )}
            </DatePickerContainer>
          )}
          {!hideAllDatesOption && (
            <FilterItemRadio
              isChecked={dirtyValue?.type === DATE_RANGE_TYPE.ALL}
              id="date-range-all"
              name="date-range-option"
              onChange={() => {
                handleChange(makeAllDateRange());
              }}
              labelContent={ALL_DATES_LABEL}
              isInvalid={validationError === ValidationErrorEnum.SELECT_ONE_OPTION}
            />
          )}
          {validationError === ValidationErrorEnum.SELECT_ONE_OPTION && (
            <Text textColor="text_error">{validationError}</Text>
          )}
        </FilterRadioContainer>
      </GenericFilterBody>
      <GenericFilterFooter footerMessage={footerMessage} />
    </GenericFilterDropdown>
  );
}
